import { CartAddEvent } from '@theme/events';

function setLineError(row, message) {
  const error = row.querySelector('[data-sc-quick-order-error-for]');
  if (!error) return;
  error.textContent = message;
  error.hidden = !message;
}

function clearErrors(root) {
  root.querySelectorAll('[data-sc-quick-order-row]').forEach((row) => setLineError(row, ''));
}

function requestedItems(root) {
  const items = [];
  let hasInvalidQuantity = false;
  root.querySelectorAll('[data-sc-quick-order-row]').forEach((row) => {
    const input = row.querySelector('[data-sc-quick-order-quantity]');
    const quantity = Number(input?.value || '0');
    const variantId = row.dataset.variantId;
    if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > 999) {
      setLineError(row, 'Enter a whole quantity from 0 to 999.');
      hasInvalidQuantity = true;
      return;
    }
    if (quantity > 0) {
      if (!variantId || row.dataset.approved !== 'true') {
        setLineError(row, 'This variant is not approved for Quick Order.');
        hasInvalidQuantity = true;
        return;
      }
      items.push({ id: Number(variantId), quantity });
    }
  });
  return { items, hasInvalidQuantity };
}

function updateStepperState(row) {
  const input = row.querySelector('[data-sc-quick-order-quantity]');
  if (!input) return;
  const quantity = Number(input.value || '0');
  const decrease = row.querySelector('[data-sc-quick-order-step="decrease"]');
  const increase = row.querySelector('[data-sc-quick-order-step="increase"]');
  if (decrease) decrease.disabled = Number.isInteger(quantity) && quantity <= 0;
  if (increase) increase.disabled = Number.isInteger(quantity) && quantity >= 999;
}

function changeQuantity(button) {
  const row = button.closest('[data-sc-quick-order-row]');
  const input = row?.querySelector('[data-sc-quick-order-quantity]');
  if (!row || !input) return;
  const current = Number(input.value || '0');
  const safeCurrent = Number.isSafeInteger(current) ? current : 0;
  const direction = button.dataset.scQuickOrderStep === 'increase' ? 1 : -1;
  input.value = String(Math.min(999, Math.max(0, safeCurrent + direction)));
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  updateStepperState(row);
}

function variantId(value) {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? String(id) : null;
}

/**
 * Returns aggregate quantities by variant ID, or null when a cart cannot be trusted.
 * Cart line items can be split by properties, so confirmation must aggregate them.
 */
export function cartVariantQuantities(cart) {
  if (!cart || !Array.isArray(cart.items) || !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) {
    return null;
  }

  const quantities = new Map();
  for (const item of cart.items) {
    const id = variantId(item?.variant_id ?? item?.id);
    const quantity = Number(item?.quantity);
    if (!id || !Number.isSafeInteger(quantity) || quantity < 0) return null;
    quantities.set(id, (quantities.get(id) || 0) + quantity);
  }
  return quantities;
}

function requestedVariantQuantities(items) {
  if (!Array.isArray(items)) return null;
  const quantities = new Map();
  for (const item of items) {
    const id = variantId(item?.id);
    const quantity = Number(item?.quantity);
    if (!id || !Number.isSafeInteger(quantity) || quantity <= 0) return null;
    quantities.set(id, (quantities.get(id) || 0) + quantity);
  }
  return quantities;
}

/**
 * Confirms that every requested variant increased by at least its requested amount.
 * The /cart/add.js response is deliberately not used as confirmation authority.
 */
export function verifyRequestedVariantDeltas(preCart, postCart, items) {
  const before = cartVariantQuantities(preCart);
  const after = cartVariantQuantities(postCart);
  const requested = requestedVariantQuantities(items);
  if (!before || !after || !requested) {
    return { confirmed: false, unconfirmedVariantIds: [] };
  }

  const unconfirmedVariantIds = [];
  requested.forEach((quantity, id) => {
    if ((after.get(id) || 0) < (before.get(id) || 0) + quantity) unconfirmedVariantIds.push(id);
  });
  return { confirmed: unconfirmedVariantIds.length === 0, unconfirmedVariantIds };
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
    .map((component) => component.dataset.sectionId)
    .filter(Boolean);
}

async function readCart() {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error('Cart could not be read.');
  const cart = await response.json();
  if (!cartVariantQuantities(cart)) throw new Error('Cart response was not valid.');
  return cart;
}

function markUnconfirmedItems(root, items, variantIds) {
  const affectedIds = variantIds.length ? new Set(variantIds) : new Set(items.map((item) => String(item.id)));
  affectedIds.forEach((id) => {
    const row = root.querySelector(`[data-variant-id="${id}"]`);
    if (row) setLineError(row, 'Shopify could not confirm this quantity. Review your cart before trying again.');
  });
}

function synchronizeCart(root, cart, sections) {
  root.dispatchEvent(
    new CartAddEvent(cart, 'quick-order', {
      source: 'quick-order',
      itemCount: cart.item_count,
      sections,
    })
  );
}

export async function submitQuickOrder(root, event) {
  event.preventDefault();
  if (root.getAttribute('aria-busy') === 'true') return;
  clearErrors(root);
  const message = root.querySelector('[data-sc-quick-order-message]');
  const submitButton = root.querySelector('button[type="submit"]');
  const { items, hasInvalidQuantity } = requestedItems(root);
  if (hasInvalidQuantity) {
    message.textContent = 'Correct the highlighted quantities before adding items.';
    return;
  }
  if (!items.length) {
    message.textContent = 'Choose a positive quantity for at least one approved variant.';
    return;
  }

  submitButton && (submitButton.disabled = true);
  root.setAttribute('aria-busy', 'true');
  message.textContent = 'Adding selected items…';

  try {
    const preCart = await readCart();
    const sections = cartSectionIds();
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      // Intentionally contains only one-time variant IDs and quantities: no selling plan and no Ka-ching properties.
      body: JSON.stringify({
        items,
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      const errorText = payload.description || payload.message || 'Shopify could not add the selected items.';
      items.forEach((item) => {
        const row = root.querySelector(`[data-variant-id="${item.id}"]`);
        if (row) setLineError(row, errorText);
      });
      message.textContent = 'No success was recorded. Review each selected line.';
      return;
    }

    let postCart;
    try {
      postCart = await readCart();
    } catch (error) {
      markUnconfirmedItems(root, items, []);
      message.textContent = 'We could not confirm the cart update. Review your cart before trying again.';
      return;
    }

    // A valid post-cart is enough to synchronize global cart UI, even if the requested delta is incomplete.
    synchronizeCart(root, postCart, payload.sections);
    const confirmation = verifyRequestedVariantDeltas(preCart, postCart, items);
    if (!confirmation.confirmed) {
      markUnconfirmedItems(root, items, confirmation.unconfirmedVariantIds);
      message.textContent = 'We could not confirm every selected quantity. Review your cart before trying again.';
      return;
    }

    message.textContent = 'Selected one-time items were added to cart.';
  } catch (error) {
    markUnconfirmedItems(root, items, []);
    message.textContent = 'We could not reach Shopify. Review your cart before trying again.';
  } finally {
    root.removeAttribute('aria-busy');
    if (submitButton) submitButton.disabled = false;
  }
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-sc-quick-order-form]').forEach((root) => {
    root.querySelector('form')?.addEventListener('submit', (event) => submitQuickOrder(root, event));
    root.querySelectorAll('[data-sc-quick-order-step]').forEach((button) => {
      button.addEventListener('click', () => changeQuantity(button));
    });
    root.querySelectorAll('[data-sc-quick-order-row]').forEach((row) => {
      row.querySelector('[data-sc-quick-order-quantity]')?.addEventListener('input', () => updateStepperState(row));
      updateStepperState(row);
    });
  });
}
